import { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { Grid, Paper, Typography } from '@mui/material';

import { Icons } from '../../../assets/icons';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { SiTrackPage } from '../../../components/si-track-page';
import { dashboardApiKeyStore } from '../../../api/dashboard-api-key-store';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
  countStyle: { color: '#0000b5', fontSize: 50 },
  iconImg: { margin: '0' },
};

interface ComponentCell {
  label: string;
  value: any;
  element: ReactElement;
}

const ComponentCell = ({ label, value, element }: ComponentCell) => {
  return (
    <Paper elevation={0} style={styling.paperStyle}>
      <Grid container direction="row" justifyContent="space-between">
        <Grid item display={'flex'}>
          <Grid container justifyContent="flex-start" flexDirection={'column'} alignItems="flex-start">
            <Typography variant="body1" color="text.secondary" className="d-flex flex-column">
              <span className="dashIcons">{element}</span>
              <label>{label}</label>
            </Typography>
          </Grid>
        </Grid>
        <Grid item display={'flex'}>
          <Grid container justifyContent="flex-end" alignItems="flex-end">
            <Typography variant="h4" style={styling.countStyle}>
              {value}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const MasterDashboard = () => {
  const { data, isLoading } = useLoadData<ApiResponse<any>>(dashboardApiKeyStore.getMasterDashboardCount());

  return (
    <SiTrackPage isLoading={isLoading} title="Master Dashboard">
      <Grid container spacing={2} className="detailsBoxContainer">
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MARKET_AREA]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/market-area" className="navLink navMarketArea">
              <ComponentCell
                label="MARKET AREAS"
                value={data?.data.marketAreaCount}
                element={<img src={Icons.Cutomer} alt="market-area" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MUNICIPALITY]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/municipality" className="navLink navMunicipality">
              <ComponentCell
                label="MUNICIPALITIES"
                value={data?.data.municipalityCount}
                element={<img src={Icons.OfficeDetails} alt="municipality" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/service" className="navLink navService">
              <ComponentCell
                label="SERVICES"
                value={data?.data.serviceCount}
                element={<img src={Icons.Project} alt="service" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SKILLSET]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/skill-set" className="navLink navSkillSet">
              <ComponentCell
                label="SKILL SETS"
                value={data?.data.skillSetCount}
                element={<img src={Icons.Units} alt="skill-set" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_ROLE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/role" className="navLink navRole">
              <ComponentCell
                label="ROLES"
                value={data?.data.roleCount}
                element={<img src={Icons.PlanReview} alt="role" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_EMPLOYEE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/employee" className="navLink navEmployee">
              <ComponentCell
                label="EMPLOYEES"
                value={data?.data.employeeCount}
                element={<img src={Icons.IconMasterEmployees} alt="employee" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_LEAVE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/manage-leave" className="navLink navManageLeave">
              <ComponentCell
                label="MANAGE LEAVES"
                value={data?.data.leaveCount}
                element={<img src={Icons.IconMasterLeaves} alt="manage-leave" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_EQUIPMENT]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/equipment" className="navLink navEquipment">
              <ComponentCell
                label="EQUIPMENTS"
                value={data?.data.equipmentCount}
                element={<img src={Icons.IconEquipments} alt="equipment" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_DIVISION]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/division" className="navLink navDivision">
              <ComponentCell
                label="DIVISIONS"
                value={data?.data.divisionCount}
                element={<img src={Icons.workorder} alt="division" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICECATEGORY]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/service-category" className="navLink navServiceCategory">
              <ComponentCell
                label="SERVICE CATEGORIES"
                value={data?.data.serviceCategoryCount}
                element={<img src={Icons.IconMasterServiceCategories} alt="service-category" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICEGROUP]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/service-group" className="navLink navServiceGroup">
              <ComponentCell
                label="SERVICE GROUPS"
                value={data?.data.serviceGroupCount}
                element={<img src={Icons.IconMasterServiceGroups} alt="service-group" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICETYPE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/service-type" className="navLink navServiceType">
              <ComponentCell
                label="SERVICE TYPES"
                value={data?.data.serviceTypeCount}
                element={<img src={Icons.IconServiceType} alt="service-type" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_REBATECOMPANY]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/rebate-company" className="navLink navRebateCompany">
              <ComponentCell
                label="REBATE COMPANIES"
                value={data?.data.rebateCount}
                element={<img src={Icons.IconRebateCompanies} alt="rebate-company" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PROJECTSUBTYPE]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/project-subtype" className="navLink navProjectSubtype">
              <ComponentCell
                label="PROJECT SUBTYPES"
                value={data?.data.projectSubTypeCount}
                element={<img src={Icons.IconProjectSubtype} alt="project-subtype" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
          <Grid item xl={4} lg={4} sm={6} xs={6} className="detailsBox">
            <NavLink to="/masters/contact" className="navLink navContact">
              <ComponentCell
                label="CONTACTS"
                value={data?.data.contactCount}
                element={<img src={Icons.IconMasterContacts} alt="contact" style={styling.iconImg} />}
              />
            </NavLink>
          </Grid>
        </GuardedComponent>
      </Grid>
    </SiTrackPage>
  );
};
